import { OverlayConfig, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  Component, Inject, OnInit, Injector, EventEmitter, OnDestroy
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { takeUntil } from 'rxjs/operators';

import { IrisButtonTypes } from '@sondermind/iris/button';
import { BreakpointsService } from '@sondermind/utilities/services';

import { ModalData, MODAL_TEMPLATE_DATA, ModalRef } from './models/modal.model';

/**
 * @title Modal Component
 */
@Component({
  selector: 'sondermind-modal',
  templateUrl: './modal.component.html',
  styleUrls: ['./modal.component.scss'],
  standalone: false
})
export class ModalComponent implements OnInit, OnDestroy {
  irisButtonTypes = IrisButtonTypes;

  private destroyed$ = new EventEmitter<void>();

  modalPortal: ComponentPortal<any>;
  emitter$ = new EventEmitter<any>();
  disableButtons: boolean = false;
  saving: boolean = false;

  private overlayConfig = new OverlayConfig();
  private overlayRef = this.overlay.create(this.overlayConfig);
  private injector = this.getInjector(this.data.templateData, new ModalRef(this.overlayRef), this.parentInjector);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ModalData<any>,
    private breakPointService: BreakpointsService,
    public dialogRef: MatDialogRef<ModalComponent>,
    private parentInjector: Injector,
    private overlay: Overlay
  ) {
    this.breakPointService.isMobile$
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((resp) => {
        this.resizeForResolution(resp);
      });

    this.emitter$
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event) => {
        this.modalPortal = new ComponentPortal(this.data.template, null, this.injector);
      });
  }

  ngOnInit(): void {
    this.modalPortal = new ComponentPortal(this.data.template, null, this.injector);
    this.data.templateData.saveEmitter$
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event) => {
        if (event === 'close') {
          this.dialogRef.close();
        }
      });

    this.data.templateData.saveClickedEmitter$
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe((event) => {
        this.disableButtons = true;
        this.saving = true;
      });
    this.data.templateData.actionBtnStatus$
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(({ style, status }) => {
        this.setActionBtnStatus(style, status);
      });
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  /**
   * Resizes modal using the breakpoint service based on if mobile or not
   */
  private resizeForResolution(isMobile: boolean): void {
    if (isMobile) {
      this.dialogRef.updateSize('100vw', '100vh');
    } else {
      this.dialogRef.updateSize('800px', this.data?.modalHeight || '480px');
    }
  }

  private getInjector(data: any, modalRef: ModalRef, parentInjector: Injector): Injector {
    // Create injector with custom injection tokens
    return Injector.create({
      parent: parentInjector,
      providers: [
        { provide: MODAL_TEMPLATE_DATA, useValue: data },
        { provide: ModalRef, useValue: modalRef }
      ]
    });
  }

  /**
   * Sets the disabled status of an action button.
   *
   * @private
   * @param {string} style
   * @param {boolean} status
   * @memberof ModalComponent
   */
  private setActionBtnStatus(style: string, status: boolean): void {
    const btn = this.data.actions.find((action) => action.style === style);

    if (btn) {
      btn.disabled = status;
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
  }
}
